import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
} from 'react-router-dom';
import ApiService from './services/api';
import Fund from './components/Fund/index';
import Intel from './components/Intel/index';
import { logout, isAuthenticated } from './utils/auth';
import NotificationProvider from './context/NotificationContext';

interface ApiToken {
  token: string;
  expiresAt: number;
}

interface ApiStatus {
  core: 'online' | 'offline' | 'loading';
  intel: 'online' | 'offline' | 'loading';
}

// Navigation component
const Navigation = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <nav className="bg-gray-800 text-white p-4">
      <div className="container mx-auto">
        <div className="flex justify-between items-center">
          <div className="text-xl font-bold">API Dashboard</div>

          {/* Mobile menu button */}
          <button
            className="md:hidden focus:outline-none"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              {isMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>

          {/* Desktop navigation */}
          <div className="hidden md:flex space-x-4 items-center">
            <Link
              to="/"
              className={`px-3 py-2 rounded ${location.pathname === '/' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
            >
              Dashboard
            </Link>
            <Link
              to="/fund"
              className={`px-3 py-2 rounded ${location.pathname.startsWith('/fund') ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
            >
              Fund Management
            </Link>
            <Link
              to="/intel"
              className={`px-3 py-2 rounded ${location.pathname.startsWith('/intel') ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
            >
              Intel
            </Link>
            <button
              onClick={handleLogout}
              className="ml-4 px-3 py-2 bg-red-600 hover:bg-red-700 rounded flex items-center"
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Logout
            </button>
          </div>
        </div>

        {/* Mobile navigation */}
        {isMenuOpen && (
          <div className="mt-4 md:hidden">
            <div className="flex flex-col space-y-2">
              <Link
                to="/"
                className={`px-3 py-2 rounded ${location.pathname === '/' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
                onClick={() => setIsMenuOpen(false)}
              >
                Dashboard
              </Link>
              <Link
                to="/fund"
                className={`px-3 py-2 rounded ${location.pathname.startsWith('/fund') ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
                onClick={() => setIsMenuOpen(false)}
              >
                Fund Management
              </Link>
              <Link
                to="/intel"
                className={`px-3 py-2 rounded ${location.pathname.startsWith('/intel') ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
                onClick={() => setIsMenuOpen(false)}
              >
                Intel
              </Link>
              <button
                onClick={handleLogout}
                className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-left flex items-center"
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Layout component
const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation />
      <main className="container mx-auto py-6">{children}</main>
    </div>
  );
};

// Login component
const Login = () => {
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token.trim()) {
      setError('Please enter a valid API token');
      return;
    }

    try {
      // Validate token by making a request
      const apiService = new ApiService(token);
      await apiService.getCoreEndpoint('/healthcheck');

      // Store token in localStorage
      const expiresAt = Date.now() + 24 * 60 * 60 * 1000; // 24 hours from now
      const tokenData: ApiToken = { token, expiresAt };
      localStorage.setItem('apiToken', token);
      localStorage.setItem('apiTokenData', JSON.stringify(tokenData));

      // Redirect to dashboard
      window.location.href = '/';
    } catch (error) {
      console.error(error);
      setError('Invalid API token');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded shadow-md w-full max-w-md">
        <h1 className="text-2xl font-bold mb-6 text-center">
          API Dashboard Login
        </h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="token"
            >
              API Token
            </label>
            <input
              id="token"
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Enter your API token"
            />
          </div>

          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
          >
            Login
          </button>
        </form>
      </div>
    </div>
  );
};

// Dashboard component
const Dashboard = () => {
  const [apiStatus, setApiStatus] = useState<ApiStatus>({
    core: 'loading',
    intel: 'loading',
  });

  useEffect(() => {
    const checkApiStatus = async () => {
      const token = localStorage.getItem('apiToken');
      if (!token) return;

      const apiService = new ApiService(token);

      // Check Core API status
      try {
        await apiService.getCoreEndpoint('/healthcheck');
        setApiStatus((prev) => ({ ...prev, core: 'online' }));
      } catch {
        setApiStatus((prev) => ({ ...prev, core: 'offline' }));
      }

      // Check Intel API status
      try {
        await apiService.getIntelEndpoint('/healthcheck');
        setApiStatus((prev) => ({ ...prev, intel: 'online' }));
      } catch {
        setApiStatus((prev) => ({ ...prev, intel: 'offline' }));
      }
    };

    checkApiStatus();

    // Check status every 30 seconds
    const interval = setInterval(checkApiStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: 'online' | 'offline' | 'loading') => {
    switch (status) {
      case 'online':
        return 'bg-green-500';
      case 'offline':
        return 'bg-red-500';
      case 'loading':
        return 'bg-yellow-500';
    }
  };

  const getStatusText = (status: 'online' | 'offline' | 'loading') => {
    switch (status) {
      case 'online':
        return 'Online';
      case 'offline':
        return 'Offline';
      case 'loading':
        return 'Checking...';
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">API Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Core API Card */}
        <div className="bg-white p-6 rounded shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Core API</h2>
            <div className="flex items-center">
              <span
                className={`inline-block w-3 h-3 rounded-full mr-2 ${getStatusColor(apiStatus.core)}`}
              ></span>
              <span>{getStatusText(apiStatus.core)}</span>
            </div>
          </div>
          <p className="text-gray-600 mb-4">
            Provides core functionality for fund management, user
            authentication, and data processing.
          </p>
          <div className="text-sm text-gray-500">
            Endpoint:{' '}
            {import.meta.env.VITE_CORE_API_URL || 'http://localhost:3000'}
          </div>
        </div>

        {/* Intel API Card */}
        <div className="bg-white p-6 rounded shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Intel API</h2>
            <div className="flex items-center">
              <span
                className={`inline-block w-3 h-3 rounded-full mr-2 ${getStatusColor(apiStatus.intel)}`}
              ></span>
              <span>{getStatusText(apiStatus.intel)}</span>
            </div>
          </div>
          <p className="text-gray-600 mb-4">
            Provides market data, analytics, and intelligence for investment
            decisions.
          </p>
          <div className="text-sm text-gray-500">
            Endpoint:{' '}
            {import.meta.env.VITE_INTEL_API_URL || 'http://localhost:3001'}
          </div>
        </div>
      </div>
    </div>
  );
};

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = isAuthenticated();
      setIsUserAuthenticated(authenticated);
      setIsAuthChecked(true);
    };

    checkAuth();
  }, []);

  if (!isAuthChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return isUserAuthenticated ? (
    <Layout>{children}</Layout>
  ) : (
    <Navigate to="/login" replace />
  );
};

// Main App component
const App = () => {
  return (
    <Router>
      <NotificationProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fund/*"
            element={
              <ProtectedRoute>
                <Fund />
              </ProtectedRoute>
            }
          />
          <Route
            path="/intel/*"
            element={
              <ProtectedRoute>
                <Intel />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </NotificationProvider>
    </Router>
  );
};

export default App;
